<template>
  <div class="card mb-4 version-panel">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h5 class="card-title mb-0">Version</h5>
      <button
        v-if="canEdit"
        class="btn btn-sm"
        :class="dirty ? 'btn-success' : 'btn-outline-secondary'"
        @click="opening = true"
      >Cut a version</button>
    </div>

    <div class="card-body">
      <!-- The link to hand playtesters. Above the version detail because
           sharing is the thing people come to this panel to do. -->
      <div class="share-row">
        <button class="btn btn-sm btn-outline-primary w-100" @click="sharePlaytest">
          {{ shared ? 'Playtest link copied' : 'Copy playtest link' }}
        </button>
        <router-link :to="`/playtest/${gameId}`" class="preview-link">Preview it</router-link>
      </div>

      <div v-if="loading" class="text-center py-2">
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading…</span>
        </div>
      </div>

      <template v-else>
        <div v-if="current" class="current">
          <span class="seq">v{{ current.sequence }}</span>
          <span class="label">{{ current.label }}</span>
          <span v-if="current.id === currentReleaseId" class="badge-published">shared</span>
        </div>
        <div v-if="current && current.deck && current.deck.color" class="deck-links small">
          Deck:
          <a :href="current.deck.color.url" target="_blank" rel="noopener">colour</a> ·
          <a v-if="current.deck.bw" :href="current.deck.bw.url" target="_blank"
             rel="noopener">black &amp; white</a> ·
          <router-link :to="`/decks/${gameId}`">all decks</router-link>
        </div>
        <p v-else class="text-muted small mb-2">
          No versions cut yet. Everything here is unreleased.
        </p>

        <!-- The whole point: which parts have moved since you handed it out.
             Cards, boards and aids each change on their own schedule, so no
             single one of them can answer this. -->
        <div v-if="dirty" class="changes">
          <div class="changes-head">Changed since {{ sinceLabel }}</div>
          <ul>
            <li v-for="line in changeLines" :key="line">{{ line }}</li>
          </ul>
        </div>
        <p v-else-if="current" class="text-success small mb-0">
          Nothing has changed since {{ sinceLabel }}.
        </p>

        <div v-if="releases.length > 1" class="history">
          <div class="changes-head">History</div>
          <ul>
            <li v-for="r in releases.slice(0, 5)" :key="r.id" class="hist-row">
              <span class="hist-main">
                <span class="seq-small">v{{ r.sequence }}</span> {{ r.label }}
                <span class="counts">
                  {{ r.counts.cards }} cards · {{ r.counts.boards }} boards ·
                  {{ r.counts.aids }} aids
                </span>
              </span>
              <span v-if="canEdit" class="hist-actions">
                <button class="btn btn-sm btn-link p-0" @click="startRename(r)">Rename</button>
                <button
                  class="btn btn-sm btn-link p-0"
                  @click="togglePublish(r)"
                >{{ r.id === currentReleaseId ? 'Unshare' : 'Share' }}</button>
                <button class="btn btn-sm btn-link text-danger p-0" @click="removeRelease(r)">
                  Delete
                </button>
              </span>
            </li>
          </ul>
        </div>

        <p v-if="error" class="text-danger small mb-0 mt-2">{{ error }}</p>
      </template>
    </div>

    <Teleport to="body">
      <div v-if="opening" class="ver-overlay" @click.self="opening = false">
        <div class="ver-dialog">
          <h5 class="mb-1">Cut a version</h5>
          <p class="text-muted small">
            Records what the rules, cards, boards and aids are right now, so
            feedback can name a version that still exists. Its cards and boards
            are kept for players to download, in colour and black and white.
          </p>

          <div class="mb-3">
            <label class="form-label small" for="verLabel">What do you call it?</label>
            <input id="verLabel" v-model="draft.label" class="form-control"
                   maxlength="80" placeholder="Playtest 3" />
            <div class="form-text">The number is assigned for you.</div>
          </div>

          <div class="mb-3">
            <label class="form-label small" for="verNotes">What changed?</label>
            <textarea id="verNotes" v-model="draft.notes" class="form-control" rows="3"
                      maxlength="2000" placeholder="Storm spaces reworded. New scoring aid."></textarea>
          </div>

          <div class="form-check mb-3">
            <input id="verPublish" v-model="draft.publish" class="form-check-input"
                   type="checkbox" />
            <label class="form-check-label small" for="verPublish">
              Share this one — it becomes the version a plain link opens.
              Leave off for an internal playtest build.
            </label>
          </div>

          <p v-if="cutError" class="text-danger small">{{ cutError }}</p>

          <div class="d-flex gap-2 justify-content-end">
            <button class="btn btn-outline-secondary btn-sm" @click="opening = false">Cancel</button>
            <button class="btn btn-primary btn-sm" :disabled="busy || !draft.label.trim()"
                    @click="cut">
              {{ busy ? 'Cutting and printing…' : 'Cut version' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { sparkforgeApi } from '@/sparkforgeApi';

export default {
  name: 'GameVersionPanel',
  props: {
    gameId: { type: String, required: true },
    canEdit: { type: Boolean, default: false }
  },
  data() {
    return {
      releases: [],
      currentReleaseId: null,
      changes: null,
      draft: { label: '', notes: '', publish: false },
      opening: false,
      loading: true,
      busy: false,
      error: null,
      cutError: null,
      shared: false
    };
  },
  computed: {
    current() {
      return this.releases[0] || null;
    },
    dirty() {
      return Boolean(this.changes && this.changes.hasUnreleasedChanges);
    },
    sinceLabel() {
      const since = this.changes && this.changes.since;
      return since ? `v${since.sequence} ${since.label}` : 'the last version';
    },
    changeLines() {
      if (!this.changes || !this.changes.changes) return [];
      const c = this.changes.changes;
      const lines = [];
      for (const part of ['cards', 'boards', 'aids']) {
        const p = c[part] || {};
        for (const [verb, names] of [['added', p.added], ['changed', p.changed],
                                     ['removed', p.removed]]) {
          if (names && names.length) {
            const shown = names.slice(0, 3).join(', ');
            const more = names.length > 3 ? ` and ${names.length - 3} more` : '';
            lines.push(`${names.length} ${part} ${verb}: ${shown}${more}`);
          }
        }
      }
      if (c.rules && c.rules.changed) lines.push('Rules edited');
      return lines;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    // Called by the parent when aids change, so the "unreleased" state does
    // not go stale while someone is still working on the page.
    async refresh() {
      await this.load();
    },
    async startRename(release) {
      const label = window.prompt('What should this version be called?', release.label);
      if (!label || label.trim() === release.label) return;
      await this.mutate(() => sparkforgeApi.updateRelease(release.id, { label: label.trim() }));
    },
    async togglePublish(release) {
      // Unsharing leaves the game with nothing shared, which is a real state:
      // a build was pulled and nothing has replaced it yet.
      await this.mutate(() => sparkforgeApi.updateRelease(release.id, {
        publish: release.id !== this.currentReleaseId
      }));
    },
    async removeRelease(release) {
      if (!window.confirm(
        `Delete v${release.sequence} "${release.label}"? ` +
        'The number is not reused, so a tester who wrote it down will not find a different one.'
      )) return;
      await this.mutate(() => sparkforgeApi.deleteRelease(release.id));
    },
    async mutate(fn) {
      this.busy = true;
      this.error = null;
      try {
        await fn();
        await this.load();
      } catch (e) {
        this.error = e.message || 'That change could not be saved.';
      } finally {
        this.busy = false;
      }
    },
    async sharePlaytest() {
      const url = `${window.location.origin}/playtest/${this.gameId}`;
      if (navigator.share) {
        try {
          await navigator.share({ url });
          return;
        } catch (e) {
          /* dismissed — fall through to copying */
        }
      }
      try {
        await navigator.clipboard.writeText(url);
        this.shared = true;
        setTimeout(() => { this.shared = false; }, 2000);
      } catch (e) {
        this.error = `Copy this link: ${url}`;
      }
    },
    async load() {
      this.loading = true;
      try {
        const [releases, changes] = await Promise.all([
          sparkforgeApi.listReleases(this.gameId),
          sparkforgeApi.unreleasedChanges(this.gameId)
        ]);
        this.releases = releases.data || [];
        this.currentReleaseId = releases.currentReleaseId || null;
        this.changes = changes;
      } catch (e) {
        this.error = e.message || 'Version history could not be loaded.';
      } finally {
        this.loading = false;
      }
    },
    async cut() {
      this.busy = true;
      this.cutError = null;
      try {
        await sparkforgeApi.cutRelease(this.gameId, {
          label: this.draft.label.trim(),
          notes: this.draft.notes.trim() || null,
          publish: this.draft.publish
        });
        this.opening = false;
        this.draft = { label: '', notes: '', publish: false };
        await this.load();
      } catch (e) {
        this.cutError = e.message || 'That version could not be cut.';
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style scoped>
.share-row { display: grid; gap: 0.35rem; margin-bottom: 0.9rem; }
.preview-link { font-size: 0.78rem; text-align: center; text-decoration: none; }

.current { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.6rem; }
.seq {
  font-family: monospace;
  font-weight: 600;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
  font-size: 0.82rem;
}
.label { font-weight: 600; font-size: 0.92rem; }
.badge-published {
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.12rem 0.4rem;
  border-radius: 4px;
  background: rgba(58, 101, 73, 0.14);
  color: #3a6549;
}

.changes, .history { margin-top: 0.75rem; }
.changes-head {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--ink-light, #8a8a8a);
  margin-bottom: 0.3rem;
}
.changes ul, .history ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.25rem;
  font-size: 0.82rem;
}
.changes li { color: var(--ink, #2b2b2b); }
.hist-row { display: flex; align-items: flex-start; gap: 0.5rem; }
.hist-main { flex: 1; min-width: 0; }
.hist-actions { display: flex; gap: 0.5rem; font-size: 0.74rem; white-space: nowrap; }
.hist-actions .btn { font-size: 0.74rem; }

.seq-small { font-family: monospace; font-size: 0.76rem; color: var(--ink-light, #8a8a8a); }
.counts { display: block; font-size: 0.72rem; color: var(--ink-light, #8a8a8a); }

.ver-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1060;
}
.ver-dialog {
  background: var(--bg-surface, #fff);
  border-radius: 12px;
  padding: 1.25rem;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
}
.deck-links { margin: -0.25rem 0 0.6rem; color: var(--ink-light, #8a8a8a); }
</style>
